import { useEffect, useState } from 'react';
import { baixarBlob } from '../../api';

/**
 * A foto do contato, do perfil do WhatsApp — onda 555 (WhatsApp completo,
 * parte A-1, Tarefa 4; spec §3.1, quadro A1).
 *
 * A foto vem pela rota do contato (`GET /comercial/contatos/:id/foto`), com a
 * regra de quem vê o contato, e não pelo id do arquivo (a API não o manda).
 * Sem foto (`temFoto` falso), ou se ela não abre, ficam as iniciais do nome;
 * o nome que é só o número (quem chegou sozinho) ganha a silhueta.
 *
 * A caixa pede a foto em toda linha: a mesma foto é baixada uma vez por aba
 * (o endereço `blob:` fica guardado por contato e não é solto — são poucas,
 * de até 1 MB, e a linha volta a aparecer a cada busca da lista).
 */

const guardadas = new Map<string, Promise<string | null>>();

function enderecoDaFoto(contatoId: string): Promise<string | null> {
  let p = guardadas.get(contatoId);
  if (!p) {
    p = baixarBlob(`/comercial/contatos/${contatoId}/foto`)
      .then((b) => (b.type.startsWith('image/') ? URL.createObjectURL(b) : null))
      /* A que não veio não fica guardada: na próxima vez, a tela pede de novo. */
      .catch(() => { guardadas.delete(contatoId); return null; });
    guardadas.set(contatoId, p);
  }
  return p;
}

/** «Ricardo Alves» → «RA»; «Ana» → «A»: as palavras que começam por letra; o número, nada (a silhueta). */
export function iniciais(nome: string | null | undefined): string {
  const t = (nome ?? '').trim();
  if (!t || /^\+?[\d ()-]+$/.test(t)) return '';
  const partes = t.split(/\s+/).filter((p) => /^\p{L}/u.test(p));
  if (!partes.length) return '';
  const primeira = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return `${primeira}${ultima}`.toUpperCase();
}

export function FotoDoContato({ contatoId, nome, temFoto, tamanho = 'm' }: {
  contatoId: string;
  nome: string;
  temFoto: boolean;
  /** `p` na linha da caixa e no cartão, `m` no cabeçalho, `g` no painel. */
  tamanho?: 'p' | 'm' | 'g';
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    setSrc(null);
    if (!temFoto) return undefined;
    let vale = true;
    void enderecoDaFoto(contatoId).then((e) => { if (vale) setSrc(e); });
    return () => { vale = false; };
  }, [contatoId, temFoto]);

  const letras = iniciais(nome);
  /* As iniciais vão pela folha (`::before` com o `data-iniciais`): fora do texto da linha da caixa e do
     cabeçalho, que leitor de tela e busca leem pelo nome. */
  return (
    <span className={`foto-do-contato ${tamanho}${src ? ' com-foto' : ''}`} aria-hidden="true"
      data-foto={src ? 'foto' : 'iniciais'} data-iniciais={!src && letras ? letras : undefined}>
      {src ? <img src={src} alt="" onError={() => setSrc(null)} />
        : letras ? null
          : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="9" r="5" />
              <path d="M2 24c0-6 4.5-9 10-9s10 3 10 9z" />
            </svg>
          )}
    </span>
  );
}
